import { createSelector } from 'reselect';
import { List, OrderedMap, Map } from 'immutable';
import get from 'lodash/get';

import Bookings from 'models/bookings';
import BookingsGroup from 'models/bookings-group';
import BookingCounts from 'models/booking-counts';
import PastBookings from 'models/past-bookings';
import { Events } from 'models/event';
import { Venues } from 'models/venue';

const readProp = (slice, key, defaultValue) => {
  if (!slice) {
    return defaultValue;
  }
  if (typeof slice.get === 'function') {
    const val = slice.get(key);
    return val !== undefined ? val : defaultValue;
  }
  return get(slice, key, defaultValue);
};

export const getBookingsState = state => get(state, 'bookings', null);

export const getBookings = createSelector(
  [getBookingsState],
  bookingsState => readProp(bookingsState, 'bookings', Bookings([])),
);

export const getUpcomingBookings = createSelector(
  [getBookingsState],
  bookingsState => readProp(bookingsState, 'upcomingBookings', new BookingsGroup()),
);

export const getPastBookings = createSelector(
  [getBookingsState],
  bookingsState => readProp(bookingsState, 'pastBookings', new PastBookings()),
);

export const getBookingCounts = createSelector(
  [getBookingsState],
  bookingsState => readProp(bookingsState, 'bookingCounts', new BookingCounts()),
);

export const getParkingPasses = createSelector(
  [getBookingsState],
  bookingsState => readProp(bookingsState, 'parkingPasses', OrderedMap()),
);

export const getSelectedParkingPassIds = createSelector(
  [getBookingsState],
  bookingsState => readProp(bookingsState, 'selectedParkingPassIds', List()),
);

export const getBookingsLocations = createSelector(
  [getBookingsState],
  bookingsState => readProp(bookingsState, 'locations', OrderedMap()),
);

export const getBookingsEvents = createSelector(
  [getBookingsState],
  bookingsState => readProp(bookingsState, 'events', Events()),
);

export const getBookingsVenue = createSelector(
  [getBookingsState],
  bookingsState => readProp(bookingsState, 'venue', null),
);

export const getBookingsVenues = createSelector(
  [getBookingsState],
  bookingsState => readProp(bookingsState, 'venues', Venues()),
);

export const getBookingsReviews = createSelector(
  [getBookingsState],
  bookingsState => readProp(bookingsState, 'reviews', Map()),
);

export const getBookingsSellers = createSelector(
  [getBookingsState],
  bookingsState => readProp(bookingsState, 'sellers', Map()),
);

export const getBookingsTickets = createSelector(
  [getBookingsState],
  bookingsState => readProp(bookingsState, 'tickets', List()),
);

export const getReceipt = createSelector(
  [getBookingsState],
  bookingsState => readProp(bookingsState, 'receipt', null),
);

export const getReceiptInitialized = createSelector(
  [getBookingsState],
  bookingsState => Boolean(readProp(bookingsState, 'receiptInitialized', false)),
);

export const getParkingPassesInitialized = createSelector(
  [getBookingsState],
  bookingsState => Boolean(readProp(bookingsState, 'parkingPassesInitialized', false)),
);

export const getCancelBookingInitialized = createSelector(
  [getBookingsState],
  bookingsState => Boolean(readProp(bookingsState, 'cancelBookingInitialized', false)),
);

export const getFrequentLocations = createSelector(
  [getBookingsState],
  bookingsState => readProp(bookingsState, 'frequentLocations', List()),
);

export const getFrequentLocationBookings = createSelector(
  [getBookingsState],
  bookingsState => readProp(bookingsState, 'frequentLocationBookings', OrderedMap()),
);

export const getReviewedBooking = createSelector(
  [getBookingsState],
  bookingsState => readProp(bookingsState, 'reviewedBooking', null),
);

export const getReviewRating = createSelector(
  [getBookingsState],
  bookingsState => readProp(bookingsState, 'reviewRating', null),
);

export const getReviewModalDismissed = createSelector(
  [getBookingsState],
  bookingsState => Boolean(readProp(bookingsState, 'reviewModalDismissed', false)),
);

export const getShouldShowActivateAccountModal = createSelector(
  [getBookingsState],
  bookingsState => Boolean(readProp(bookingsState, 'shouldShowActivateAccountModal', false)),
);

export const getActivateAccountModalDismissed = createSelector(
  [getBookingsState],
  bookingsState => Boolean(readProp(bookingsState, 'activateAccountModalDismissed', false)),
);

const getSelectedElements = (state, props = {}, key, mapper) => {
  const bookingsState = getBookingsState(state);
  const elements = readProp(bookingsState, key, null);
  let selected = new List();

  const selectedParkingPassIds = get(props, 'selectedParkingPassIds') || getSelectedParkingPassIds(state);

  if (elements && selectedParkingPassIds && typeof selectedParkingPassIds.map === 'function') {
    selected = selectedParkingPassIds
      .map(id => mapper(id, elements))
      .filter(booking => booking);
  }

  return selected;
};

/**
 * Gets Bookings for Selected Parking Passes
 * @param  {ReduxStore} state
 *         Redux store
 * @param  {Object} props
 *         Component Props
 * @return {List}
 *         List of Bookings for Selected Parking Passes
 */
export const getSelectedBookings = (state, props = {}) => (
  getSelectedElements(state, props, 'bookings', (id, elements) => elements.get(id))
);

/**
 * Gets Selected Parking Passes
 * @param  {ReduxStore} state
 *         Redux store
 * @param  {Object} props
 *         Component Props
 * @return {List}
 *         List of Selected Parking Passes
 */
export const getSelectedParkingPasses = (state, props = {}) => (
  getSelectedElements(state, props, 'parkingPasses', (id, elements) => elements.get(id.toString()))
);
